import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { BlockRenderer } from "./blocks/BlockRenderer";
import {
  toSeverity,
  type NotificationRow,
  type Severity,
} from "./blocks/schema";
import { InformationalToggle } from "./InformationalToggle";
import type { ReactElement } from "react";

import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnseenCount,
} from "@/api/notifications";
import { ErrorOutlineIcon, InfoOutlinedIcon, WarningAmberIcon } from "@/icons";
import dayjs from "@/utils/configuredDayjs";

const SEVERITY_ICON: Record<Severity, ReactElement> = {
  error: <ErrorOutlineIcon fontSize="small" color="error" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
  info: <InfoOutlinedIcon fontSize="small" color="info" />,
};

const humanizeType = (type: string) => {
  const words = type.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

interface NotificationPanelProps {
  siteId: string;
  onNavigate?: () => void;
}

export function NotificationPanel({
  siteId,
  onNavigate,
}: NotificationPanelProps) {
  const { data, isPending } = useNotifications(siteId);
  const { data: unseenCount = 0 } = useUnseenCount(siteId);
  const markRead = useMarkRead(siteId);
  const markAllRead = useMarkAllRead(siteId);

  const rows = data?.notifications ?? [];

  const markRowRead = (row: NotificationRow) => {
    if (!row.read_at) markRead.mutate(row.id);
  };

  return (
    <Box
      data-testid="notification-panel"
      sx={{ width: 420, maxWidth: "100vw" }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 6,
          py: 4,
        }}
      >
        <Typography variant="subtitle1">Notifications</Typography>
        <InformationalToggle siteId={siteId} />
      </Stack>
      <Divider />
      {rows.length > 0 && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            px: 6,
            py: 3,
            backgroundColor: "background.paper-elevation-0",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {unseenCount === 0 ? "All caught up" : `${unseenCount} unread`}
          </Typography>
          <Button
            data-testid="notification-mark-all-read"
            size="small"
            variant="text"
            disabled={unseenCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
            sx={{ px: 3, py: 2, minWidth: 0 }}
          >
            Mark all as read
          </Button>
        </Stack>
      )}
      {isPending ? (
        <Box
          data-testid="notification-loading"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography
          data-testid="notification-empty"
          variant="body2"
          color="text.secondary"
          sx={{ px: 6, py: 8, textAlign: "center" }}
        >
          Nothing to catch up on.
        </Typography>
      ) : (
        <Stack role="list" sx={{ maxHeight: 520, overflowY: "auto" }}>
          {rows.map((row) => (
            <NotificationItem
              key={row.id}
              row={row}
              onClick={() => markRowRead(row)}
              onOpenUrl={() => {
                markRowRead(row);
                onNavigate?.();
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

interface NotificationItemProps {
  row: NotificationRow;
  onClick: () => void;
  onOpenUrl: () => void;
}

export function NotificationItem({
  row,
  onClick,
  onOpenUrl,
}: NotificationItemProps) {
  const unread = !row.read_at;
  const stamp = [
    humanizeType(row.type),
    row.brand_name,
    row.occurred_at ? dayjs(row.occurred_at).fromNow() : "",
  ].filter(Boolean);

  return (
    <Box
      data-testid={`notification-row-${row.id}`}
      role="listitem"
      sx={{
        display: "flex",
        gap: 4,
        px: 6,
        py: 5,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: unread ? "primary._states.hover" : "transparent",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box sx={{ flex: "none", display: "flex", pt: 2 }}>
        {SEVERITY_ICON[toSeverity(row.severity)]}
      </Box>
      <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={3}
          data-testid={`notification-row-${row.id}-header`}
          role="button"
          tabIndex={0}
          aria-label={unread ? `Mark "${row.title}" as read` : row.title}
          onClick={onClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }}
          sx={{
            alignItems: "flex-start",
            cursor: unread ? "pointer" : "default",
            "&:hover": { backgroundColor: "action.hover" },
            "&:focus-visible": {
              outline: 2,
              outlineOffset: 2,
              outlineColor: "primary.main",
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
          >
            {row.title}
          </Typography>
          {unread && (
            <Box
              sx={{
                flex: "none",
                mt: 2,
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "primary.main",
              }}
            />
          )}
        </Stack>
        <BlockRenderer blocks={row.blocks} onOpenUrl={onOpenUrl} />
        <Typography variant="body3" color="text.secondary">
          {stamp.join(" · ")}
        </Typography>
      </Stack>
    </Box>
  );
}
